import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus, Save, Shield, Ban, Send, Upload, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

export default function AdminPage() {
  const { user, loading } = useAuth();

  if (loading) return <div className="py-24 text-center text-muted-foreground">Chargement...</div>;
  const STAFF = ["fondateur", "co_fondateur", "superadmin", "admin", "moderateur_plus", "moderateur", "moderateur_essai"];
  if (!user || !STAFF.includes(user.role)) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <Shield className="h-12 w-12 mx-auto text-primary mb-3" />
        <p className="font-display uppercase text-2xl mb-2">Accès réservé</p>
        <p className="text-muted-foreground text-sm">Cette section est réservée au staff de RiverSide Roleplay.</p>
      </div>
    );
  }

  const canShop = ["fondateur", "co_fondateur"].includes(user.role);
  const canAdmin = ["fondateur", "co_fondateur", "superadmin", "admin"].includes(user.role);
  const defaultTab = canShop ? "news" : canAdmin ? "news" : "tickets";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl uppercase tracking-tight">{canAdmin ? "Administration" : "Panel Staff"}</h1>
        <Badge variant="outline" className="border-primary/40 text-primary" data-testid="my-role-badge">{user.role_label || user.role}</Badge>
      </div>
      <Tabs defaultValue={defaultTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          {canAdmin && <TabsTrigger value="news" data-testid="admin-tab-news">Actualités</TabsTrigger>}
          {canAdmin && <TabsTrigger value="rules" data-testid="admin-tab-rules">Règlement</TabsTrigger>}
          {canShop && <TabsTrigger value="shop" data-testid="admin-tab-shop">Boutique</TabsTrigger>}
          {canShop && <TabsTrigger value="orders" data-testid="admin-tab-orders">Ventes</TabsTrigger>}
          <TabsTrigger value="tickets" data-testid="admin-tab-tickets">Tickets</TabsTrigger>
          <TabsTrigger value="bans" data-testid="admin-tab-bans">Sanctions</TabsTrigger>
          {canAdmin && <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>}
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Signalements</TabsTrigger>
          {canShop && <TabsTrigger value="settings" data-testid="admin-tab-settings">Paramètres</TabsTrigger>}
        </TabsList>
        {canAdmin && <TabsContent value="news"><NewsAdmin /></TabsContent>}
        {canAdmin && <TabsContent value="rules"><RulesAdmin /></TabsContent>}
        {canShop && <TabsContent value="shop"><ShopAdmin /></TabsContent>}
        {canShop && <TabsContent value="orders"><OrdersAdmin /></TabsContent>}
        <TabsContent value="tickets"><TicketsAdmin /></TabsContent>
        <TabsContent value="bans"><BansAdmin /></TabsContent>
        {canAdmin && <TabsContent value="users"><UsersAdmin currentUser={user} /></TabsContent>}
        <TabsContent value="reports"><ReportsAdmin /></TabsContent>
        {canShop && <TabsContent value="settings"><SettingsAdmin /></TabsContent>}
      </Tabs>
    </div>
  );
}

function NewsAdmin() {
  const { data: news = [], refetch } = useQuery({ queryKey: ["admin-news"], queryFn: () => api.get("/news").then((r) => r.data) });
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", kind: "news" });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (form.title.length < 3) return toast.error("Titre trop court");
    try {
      if (editing) {
        await api.put(`/news/${editing}`, form);
        toast.success("Actualité mise à jour");
      } else {
        await api.post("/news", form);
        toast.success("Actualité publiée");
      }
      setForm({ title: "", excerpt: "", content: "", kind: "news" });
      setEditing(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette actualité ?")) return;
    await api.delete(`/news/${id}`);
    toast.success("Supprimée"); refetch();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm">{editing ? "Modifier" : "Publier"} une actualité</p>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" data-testid="admin-news-title" />
          <Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Résumé court" />
          <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Contenu" rows={6} />
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="news">Actualité</SelectItem>
              <SelectItem value="event">Événement</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button onClick={save} data-testid="admin-news-save"><Save className="h-3.5 w-3.5 mr-1" />{editing ? "Enregistrer" : "Publier"}</Button>
            {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm({ title: "", excerpt: "", content: "", kind: "news" }); }}>Annuler</Button>}
          </div>
        </CardContent>
      </Card>
      <div className="space-y-3">
        {news.map((n) => (
          <Card key={n.id}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="flex-1">
                <Badge variant="outline" className="mb-1">{n.kind}</Badge>
                <p className="font-medium">{n.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{n.excerpt}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(n.id); setForm({ title: n.title, excerpt: n.excerpt, content: n.content, kind: n.kind }); }}>Éditer</Button>
                <Button size="sm" variant="ghost" onClick={() => del(n.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function RulesAdmin() {
  const { data, refetch } = useQuery({ queryKey: ["admin-rules"], queryFn: () => api.get("/rules").then((r) => r.data) });
  const [cats, setCats] = useState([]);
  useEffect(() => { if (data?.categories) setCats(JSON.parse(JSON.stringify(data.categories))); }, [data]);

  const save = async () => {
    try {
      await api.put("/rules", { categories: cats });
      toast.success("Règlement mis à jour");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const updateRule = (ci, ri, val) => {
    const copy = [...cats]; copy[ci].rules[ri] = val; setCats(copy);
  };
  const addRule = (ci) => { const copy = [...cats]; copy[ci].rules.push(""); setCats(copy); };
  const removeRule = (ci, ri) => { const copy = [...cats]; copy[ci].rules.splice(ri, 1); setCats(copy); };
  const updateTitle = (ci, val) => { const copy = [...cats]; copy[ci].title = val; setCats(copy); };
  const addCategory = () => setCats([...cats, { id: `cat_${Date.now()}`, title: "Nouvelle catégorie", rules: [""], important: false }]);
  const removeCategory = (ci) => { if (window.confirm("Supprimer cette catégorie ?")) { const copy = [...cats]; copy.splice(ci, 1); setCats(copy); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Modifiez le règlement. La date de mise à jour sera automatiquement rafraîchie.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addCategory}><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une catégorie</Button>
          <Button onClick={save} data-testid="admin-rules-save"><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
        </div>
      </div>
      {cats.map((c, ci) => (
        <Card key={c.id}>
          <CardContent className="p-5 space-y-2">
            <div className="flex gap-2">
              <Input value={c.title} onChange={(e) => updateTitle(ci, e.target.value)} className="font-display font-bold" />
              <Button variant="ghost" onClick={() => removeCategory(ci)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
            {c.rules.map((r, ri) => (
              <div key={ri} className="flex gap-2">
                <Textarea value={r} onChange={(e) => updateRule(ci, ri, e.target.value)} rows={2} className="flex-1" />
                <Button size="icon" variant="ghost" onClick={() => removeRule(ci, ri)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => addRule(ci)}><Plus className="h-3.5 w-3.5 mr-1" /> Règle</Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function UsersAdmin({ currentUser }) {
  const { data: users = [], refetch } = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const { data: meta } = useQuery({ queryKey: ["admin-roles"], queryFn: () => api.get("/admin/roles").then((r) => r.data) });

  const setRole = async (steamId, role) => {
    try {
      await api.put(`/admin/users/${steamId}/role`, { role });
      toast.success("Rôle mis à jour"); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const assignableRoles = (meta?.roles || []).filter((r) => r.assignable);
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <Card key={u.steam_id64}>
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <p className="font-medium">{u.persona_name}</p>
              <p className="text-xs text-muted-foreground font-mono-tech">{u.steam_id64}</p>
              <p className="text-[10px] text-primary uppercase tracking-widest font-mono-tech mt-1">{u.role_label || u.role}</p>
            </div>
            <Select value={u.role} onValueChange={(v) => setRole(u.steam_id64, v)} disabled={u.steam_id64 === currentUser.steam_id64}>
              <SelectTrigger className="w-48" data-testid={`role-select-${u.steam_id64}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {assignableRoles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ReportsAdmin() {
  const { data: reports = [], refetch } = useQuery({ queryKey: ["admin-reports"], queryFn: () => api.get("/admin/reports").then((r) => r.data) });
  const resolve = async (id) => { await api.post(`/admin/reports/${id}/resolve`); toast.success("Signalement résolu"); refetch(); };

  if (reports.length === 0) return <Card><CardContent className="p-8 text-center text-muted-foreground">Aucun signalement en attente.</CardContent></Card>;

  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <Card key={r.id}>
          <CardContent className="p-4">
            <div className="flex justify-between items-start gap-3">
              <div>
                <p className="text-sm"><span className="text-muted-foreground">Signalé par :</span> {r.reporter.persona_name}</p>
                <p className="text-sm mt-1"><span className="text-muted-foreground">Motif :</span> {r.reason}</p>
                <p className="text-xs text-muted-foreground mt-1 font-mono-tech">Post {r.post_id}</p>
              </div>
              <Button size="sm" onClick={() => resolve(r.id)}>Marquer résolu</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function BansAdmin() {
  const { data: bans = [], refetch } = useQuery({ queryKey: ["admin-bans"], queryFn: () => api.get("/admin/bans").then((r) => r.data) });
  const [form, setForm] = useState({ steam_id64: "", persona_name: "", reason: "", duration_days: "" });

  const create = async () => {
    if (!/^\d{17}$/.test(form.steam_id64)) return toast.error("SteamID64 invalide (17 chiffres)");
    if (form.reason.length < 3) return toast.error("Motif trop court");
    try {
      const payload = { ...form, duration_days: form.duration_days ? parseInt(form.duration_days) : null };
      await api.post("/admin/bans", payload);
      toast.success("Sanction ajoutée"); setForm({ steam_id64: "", persona_name: "", reason: "", duration_days: "" });
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Révoquer cette sanction ?")) return;
    await api.delete(`/admin/bans/${id}`); toast.success("Révoquée"); refetch();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm flex items-center gap-2"><Ban className="h-4 w-4 text-primary" /> Nouvelle sanction</p>
          <Input value={form.steam_id64} onChange={(e) => setForm({ ...form, steam_id64: e.target.value })} placeholder="SteamID64 (17 chiffres)" maxLength={17} data-testid="ban-steamid" />
          <Input value={form.persona_name} onChange={(e) => setForm({ ...form, persona_name: e.target.value })} placeholder="Pseudo (optionnel)" />
          <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Motif de la sanction" rows={3} data-testid="ban-reason" />
          <Input type="number" min={1} value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} placeholder="Durée en jours (vide = permanent)" />
          <Button onClick={create} data-testid="ban-create">Ajouter la sanction</Button>
        </CardContent>
      </Card>
      <div className="space-y-2">
        {bans.length === 0 && <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Aucune sanction enregistrée.</CardContent></Card>}
        {bans.map((b) => (
          <Card key={b.id}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap gap-2 items-center mb-1">
                  <span className="font-medium">{b.persona_name || "—"}</span>
                  <span className="font-mono-tech text-xs text-muted-foreground">{b.steam_id64}</span>
                  {b.active ? <Badge variant="destructive">Actif</Badge> : <Badge variant="outline">Levé/expiré</Badge>}
                  {b.permanent ? <Badge variant="outline">Permanent</Badge> : <Badge variant="outline">{b.expires_at ? new Date(b.expires_at).toLocaleDateString("fr-FR") : "—"}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{b.reason}</p>
                <p className="text-xs text-muted-foreground mt-1">par {b.issued_by}</p>
              </div>
              {b.active && <Button size="sm" variant="ghost" onClick={() => revoke(b.id)} className="text-destructive">Révoquer</Button>}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function TicketsAdmin() {
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const { data: types = [] } = useQuery({ queryKey: ["ticket-types"], queryFn: () => api.get("/tickets/types").then((r) => r.data) });
  const { data: tickets = [], refetch } = useQuery({
    queryKey: ["admin-tickets", status, type],
    queryFn: () => api.get(`/tickets/all${status || type ? `?${status ? `status=${status}&` : ""}${type ? `type=${type}` : ""}` : ""}`).then((r) => r.data),
  });

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); refetch(); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="open">Ouvert</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="closed">Fermé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); refetch(); }}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {tickets.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Aucun ticket.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <Link key={t.id} to={`/tickets/${t.id}`}>
              <Card className="hover:border-primary/60">
                <CardContent className="p-4 flex justify-between items-center">
                  <div>
                    <div className="flex gap-2 items-center mb-1">
                      <Badge variant="outline">{t.type_label}</Badge>
                      <Badge variant={t.status === "closed" ? "outline" : "default"}>{t.status}</Badge>
                    </div>
                    <p className="font-medium">{t.subject}</p>
                    <p className="text-xs text-muted-foreground">par {t.author.persona_name} · {t.messages?.length || 0} message(s)</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsAdmin() {
  const { data, refetch } = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const [webhook, setWebhook] = useState("");
  const [workshop, setWorkshop] = useState("");
  const [maintenanceAll, setMaintenanceAll] = useState(false);
  const [maintenancePages, setMaintenancePages] = useState([]);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  useEffect(() => {
    if (data) {
      setWebhook(data.discord_staff_webhook || "");
      setWorkshop(data.workshop_url || "");
      setMaintenanceAll(!!data.maintenance_all);
      setMaintenancePages(data.maintenance_pages || []);
      setMaintenanceMessage(data.maintenance_message || "");
    }
  }, [data]);

  const PAGE_OPTIONS = [
    { key: "home", label: "Accueil" },
    { key: "reglement", label: "Règlement" },
    { key: "forum", label: "Forum" },
    { key: "boutique", label: "Boutique" },
    { key: "sanctions", label: "Sanctions" },
    { key: "tickets", label: "Tickets" },
  ];
  const togglePage = (k) => setMaintenancePages((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        discord_staff_webhook: webhook,
        workshop_url: workshop,
        maintenance_all: maintenanceAll,
        maintenance_pages: maintenancePages,
        maintenance_message: maintenanceMessage,
      });
      toast.success("Paramètres enregistrés");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const test = async () => {
    try { await api.post("/admin/settings/test-webhook"); toast.success("Message test envoyé sur Discord"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const downloadAddon = async () => {
    try {
      const r = await api.get("/admin/gmod-addon", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "riverside_web_bans.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Addon téléchargé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm">Webhook Discord (staff)</p>
          <p className="text-xs text-muted-foreground">Source active : <span className="font-mono-tech text-primary">{data?.webhook_source || "…"}</span></p>
          <Input value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="https://discord.com/api/webhooks/…" data-testid="settings-webhook-input" />
          <div className="flex gap-2">
            <Button onClick={save} data-testid="settings-webhook-save"><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer tout</Button>
            <Button variant="outline" onClick={test} data-testid="settings-webhook-test"><Send className="h-3.5 w-3.5 mr-1" /> Tester</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm">Workshop Steam</p>
          <p className="text-xs text-muted-foreground">Collez l'URL de votre collection Workshop Steam. Un onglet "Workshop ↗" apparaîtra dans la nav.</p>
          <Input value={workshop} onChange={(e) => setWorkshop(e.target.value)} placeholder="https://steamcommunity.com/sharedfiles/filedetails/?id=…" data-testid="settings-workshop-input" />
          <Button onClick={save} data-testid="settings-workshop-save"><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm">Mode maintenance</p>
          <p className="text-xs text-muted-foreground">Le staff garde toujours accès. Les visiteurs voient un écran "maintenance en cours".</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={maintenanceAll} onChange={(e) => setMaintenanceAll(e.target.checked)} data-testid="maintenance-all" />
            <span>Site entier en maintenance</span>
          </label>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-2 mb-1">Ou seulement certaines pages :</p>
            <div className="flex flex-wrap gap-2">
              {PAGE_OPTIONS.map((p) => (
                <button
                  key={p.key} type="button" onClick={() => togglePage(p.key)}
                  data-testid={`maintenance-page-${p.key}`}
                  className={`px-3 py-1 rounded-full text-sm border ${maintenancePages.includes(p.key) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Message affiché (optionnel)</p>
            <Textarea value={maintenanceMessage} onChange={(e) => setMaintenanceMessage(e.target.value)} rows={3} placeholder="Retour prévu à 20h…" data-testid="maintenance-message" />
          </div>
          <Button onClick={save} data-testid="settings-maintenance-save"><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm">Addon Garry's Mod</p>
          <p className="text-xs text-muted-foreground">Sync bidirectionnelle bans + commandes boutique. Token intégré automatiquement dans le zip.</p>
          <Button onClick={downloadAddon} data-testid="download-gmod-addon"><Save className="h-3.5 w-3.5 mr-1" /> Télécharger riverside_web_bans.zip</Button>
        </CardContent>
      </Card>
    </div>
  );
}


function ShopAdmin() {
  const { data: items = [], refetch } = useQuery({ queryKey: ["admin-shop"], queryFn: () => api.get("/admin/shop/items").then((r) => r.data) });
  const { data: categories = [], refetch: refetchCats } = useQuery({ queryKey: ["admin-shop-categories"], queryFn: () => api.get("/shop/categories").then((r) => r.data) });
  const emptyForm = {
    title: "", description: "", image_url: "",
    price_eur: 5.0, kind: "one_time", interval: "month",
    commands: "", amount_var: "", active: true, order: 0, category_slug: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", slug: "", description: "", order: 0 });

  const load = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title, description: item.description || "", image_url: item.image_url || "",
      price_eur: item.price_eur, kind: item.kind, interval: item.interval || "month",
      commands: (item.commands || []).join("\n"),
      amount_var: item.amount_var ?? "",
      active: item.active, order: item.order || 0,
      category_slug: item.category_slug || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const reset = () => { setEditingId(null); setForm(emptyForm); };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (form.title.length < 2) return toast.error("Titre trop court");
    if (!form.price_eur || Number(form.price_eur) <= 0) return toast.error("Prix invalide");
    const payload = {
      title: form.title,
      description: form.description,
      image_url: form.image_url || null,
      price_eur: Number(form.price_eur),
      kind: form.kind,
      interval: form.kind === "subscription" ? form.interval : null,
      commands: form.commands.split("\n").map((c) => c.trim()).filter(Boolean),
      amount_var: form.amount_var === "" ? null : Number(form.amount_var),
      active: !!form.active,
      order: Number(form.order) || 0,
      category_slug: form.category_slug || null,
    };
    try {
      if (editingId) { await api.put(`/admin/shop/items/${editingId}`, payload); toast.success("Article mis à jour"); }
      else { await api.post("/admin/shop/items", payload); toast.success("Article publié"); }
      reset(); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet article ? Le prix Stripe correspondant sera désactivé.")) return;
    await api.delete(`/admin/shop/items/${id}`); toast.success("Article supprimé"); refetch();
  };

  const testDelivery = async () => {
    if (!editingId) return toast.error("Enregistrez l'article avant de tester");
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/shop/items/${editingId}/test-delivery`);
      toast.success(`Envoi de ${data.commands.length} commande(s) sur votre SteamID`, {
        description: data.commands.join(" • "), duration: 8000,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setTesting(false); }
  };

  const createCategory = async () => {
    if (newCat.name.length < 2) return toast.error("Nom trop court");
    const slug = (newCat.slug || newCat.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    if (!slug) return toast.error("Slug invalide");
    try {
      await api.post("/admin/shop/categories", { ...newCat, slug });
      toast.success("Catégorie créée");
      setNewCat({ name: "", slug: "", description: "", order: 0 });
      refetchCats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const delCategory = async (slug) => {
    if (!window.confirm("Supprimer cette catégorie ? Les articles seront déclassés (sans catégorie).")) return;
    await api.delete(`/admin/shop/categories/${slug}`); toast.success("Catégorie supprimée"); refetchCats();
  };

  return (
    <div className="space-y-6">
      {/* Categories management */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="font-display uppercase text-sm">Catégories</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {categories.length === 0 && <p className="text-xs text-muted-foreground">Aucune catégorie. Créez la première ci-dessous.</p>}
            {categories.map((c) => (
              <Badge key={c.slug} variant="outline" className="pl-3 pr-1 py-1 h-auto" data-testid={`cat-badge-${c.slug}`}>
                {c.name} <span className="text-muted-foreground ml-2 font-mono-tech text-[10px]">{c.slug}</span>
                <button onClick={() => delCategory(c.slug)} className="ml-2 text-destructive hover:opacity-80"><Trash2 className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
          <div className="grid sm:grid-cols-4 gap-2">
            <Input placeholder="Nom (ex: Crédits IG)" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="cat-new-name" />
            <Input placeholder="slug-optionnel" value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} />
            <Input placeholder="Description courte" value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} />
            <Button onClick={createCategory} data-testid="cat-new-submit"><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2">
          <CardContent className="p-5 space-y-3">
            <p className="font-display uppercase text-sm flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" /> {editingId ? "Modifier" : "Nouvel"} article
            </p>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Titre</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="shop-title" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Catégorie</label>
              <Select value={form.category_slug || "none"} onValueChange={(v) => setForm({ ...form, category_slug: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="shop-category"><SelectValue placeholder="Sans catégorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sans catégorie</SelectItem>
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Image</label>
              <div className="flex gap-2 items-center">
                <Input placeholder="URL image ou téléversez ci-contre" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="flex-1" data-testid="shop-image-url" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={upload} className="hidden" data-testid="shop-image-upload" />
                  <span className="inline-flex items-center gap-1 h-10 px-3 rounded-md border border-input bg-background hover:bg-secondary text-sm">
                    <Upload className="h-3.5 w-3.5" /> {uploading ? "…" : "Fichier"}
                  </span>
                </label>
              </div>
              {form.image_url && <img src={form.image_url} alt="preview" className="mt-2 w-full max-h-40 object-cover rounded" />}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Prix (€)</label>
                <Input type="number" step="0.01" min="0.5" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} data-testid="shop-price" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Type</label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="shop-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">Paiement unique</SelectItem>
                    <SelectItem value="subscription">Abonnement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.kind === "subscription" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Renouvellement</label>
                <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Mensuel</SelectItem>
                    <SelectItem value="year">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">
                Commandes serveur (une par ligne)
              </label>
              <Textarea
                value={form.commands} onChange={(e) => setForm({ ...form, commands: e.target.value })}
                placeholder={"Exemples :\nsam addmoney {steamid64} {amount}\nsam setrank {steamid64} vip"}
                rows={4} className="font-mono-tech text-xs" data-testid="shop-commands"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Variables : <code className="text-primary">{"{steamid64}"}</code>, <code className="text-primary">{"{amount}"}</code>, <code className="text-primary">{"{title}"}</code></p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Montant custom (optionnel)</label>
                <Input type="number" value={form.amount_var} onChange={(e) => setForm({ ...form, amount_var: e.target.value })} placeholder="ex: 1000" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-1 block">Ordre d'affichage</label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Article visible dans la boutique
            </label>
            <div className="flex gap-2 pt-2 flex-wrap">
              <Button onClick={save} data-testid="shop-save"><Save className="h-3.5 w-3.5 mr-1" />{editingId ? "Enregistrer" : "Publier"}</Button>
              {editingId && (
                <Button variant="outline" onClick={testDelivery} disabled={testing} data-testid="shop-test-delivery">
                  <Send className="h-3.5 w-3.5 mr-1" /> {testing ? "Envoi…" : "Test livraison"}
                </Button>
              )}
              {editingId && <Button variant="ghost" onClick={reset}>Annuler</Button>}
            </div>
            {editingId && (
              <p className="text-[11px] text-muted-foreground">Le test envoie réellement les commandes au serveur en vous ciblant. Vous devez être connecté au serveur Garry's Mod.</p>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-3 space-y-2">
          {items.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Aucun article. Créez le premier via le formulaire.</CardContent></Card>
          ) : items.map((it) => (
            <Card key={it.id} className={!it.active ? "opacity-60" : ""}>
              <CardContent className="p-4 flex items-start gap-3">
                {it.image_url && <img src={it.image_url} alt="" className="w-24 h-16 object-cover rounded shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex gap-2 items-center flex-wrap">
                    <p className="font-medium">{it.title}</p>
                    <Badge variant={it.kind === "subscription" ? "default" : "outline"}>{it.kind === "subscription" ? `Abo ${it.interval}` : "Unique"}</Badge>
                    {it.category_slug && <Badge variant="outline" className="border-primary/40 text-primary">{categories.find((c) => c.slug === it.category_slug)?.name || it.category_slug}</Badge>}
                    {!it.active && <Badge variant="outline">Masqué</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">{it.description}</p>
                  <p className="text-xs font-mono-tech text-primary mt-1">{it.price_eur.toFixed(2)} € · {(it.commands || []).length} commande(s)</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => load(it)}>Éditer</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(it.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Promo codes */}
      <PromosAdmin />
    </div>
  );
}

function PromosAdmin() {
  const { data: promos = [], refetch } = useQuery({ queryKey: ["admin-promos"], queryFn: () => api.get("/admin/shop/promos").then((r) => r.data) });
  const [form, setForm] = useState({ code: "", percent_off: "", amount_off_eur: "", max_redemptions: "", valid_until: "", active: true });

  const create = async () => {
    if (form.code.length < 2) return toast.error("Code trop court");
    if (!form.percent_off && !form.amount_off_eur) return toast.error("Renseignez pourcentage ou montant");
    try {
      await api.post("/admin/shop/promos", {
        code: form.code,
        percent_off: form.percent_off ? Number(form.percent_off) : null,
        amount_off_eur: form.amount_off_eur ? Number(form.amount_off_eur) : null,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        active: form.active,
      });
      toast.success("Code promo créé");
      setForm({ code: "", percent_off: "", amount_off_eur: "", max_redemptions: "", valid_until: "", active: true });
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce code ? Il sera désactivé sur Stripe.")) return;
    await api.delete(`/admin/shop/promos/${id}`); toast.success("Supprimé"); refetch();
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <p className="font-display uppercase text-sm">Codes promo</p>
        <div className="grid sm:grid-cols-6 gap-2">
          <Input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="uppercase font-mono-tech" data-testid="promo-code" />
          <Input type="number" min="1" max="100" placeholder="% off" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value, amount_off_eur: "" })} data-testid="promo-percent" />
          <Input type="number" step="0.01" min="0.5" placeholder="€ off" value={form.amount_off_eur} onChange={(e) => setForm({ ...form, amount_off_eur: e.target.value, percent_off: "" })} />
          <Input type="number" placeholder="Max utilisations" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} />
          <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
          <Button onClick={create} data-testid="promo-create"><Plus className="h-3.5 w-3.5 mr-1" /> Créer</Button>
        </div>
        <div className="space-y-2">
          {promos.length === 0 && <p className="text-xs text-muted-foreground">Aucun code promo pour l'instant.</p>}
          {promos.map((p) => (
            <div key={p.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2">
              <div className="flex items-center gap-3 flex-wrap">
                <code className="font-mono-tech text-primary font-bold">{p.code}</code>
                <span className="text-sm">{p.percent_off ? `-${p.percent_off}%` : `-${p.amount_off_eur?.toFixed(2)} €`}</span>
                <span className="text-xs text-muted-foreground">Utilisé {p.times_redeemed || 0}{p.max_redemptions ? `/${p.max_redemptions}` : ""}</span>
                {p.valid_until && <span className="text-xs text-muted-foreground">jusqu'au {new Date(p.valid_until).toLocaleDateString("fr-FR")}</span>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OrdersAdmin() {
  const { data: orders = [], refetch } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: () => api.get("/admin/orders").then((r) => r.data),
    refetchInterval: 15000,
  });
  if (orders.length === 0) return <Card><CardContent className="p-8 text-center text-muted-foreground">Aucune vente pour l'instant.</CardContent></Card>;
  return (
    <div className="space-y-2">
      {orders.map((o) => {
        const paid = o.payment_status === "paid";
        return (
          <Card key={o.id}>
            <CardContent className="p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex gap-2 flex-wrap items-center mb-1">
                  <Badge variant={paid ? "default" : "outline"}>{o.payment_status}</Badge>
                  <Badge variant="outline">{o.mode}</Badge>
                  {paid && (
                    o.commands_delivered
                      ? <Badge variant="outline" className="border-primary/50 text-primary">Livré en jeu</Badge>
                      : o.commands_pending > 0
                      ? <Badge variant="outline" className="border-yellow-500/50 text-yellow-600">En attente ({o.commands_pending})</Badge>
                      : <Badge variant="outline">Aucune commande</Badge>
                  )}
                </div>
                <p className="font-medium">{o.shop_item_title}</p>
                <p className="text-xs text-muted-foreground">{o.user_name} · <span className="font-mono-tech">{o.steam_id64}</span> · {new Date(o.created_at).toLocaleString("fr-FR")}</p>
                {paid && o.commands_pending > 0 && (
                  <p className="text-xs text-yellow-600 mt-1">Le joueur doit être connecté au serveur pour recevoir ses commandes (elles seront exécutées automatiquement à sa prochaine connexion).</p>
                )}
              </div>
              <p className="font-display text-xl text-primary">{o.amount_eur?.toFixed(2)} €</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

